import React from 'react';

import FullLogo from 'assets/images/logos/FullLogo.png';
import FitLogo from 'assets/images/logos/FitLogo.png';

import { Link, useNavigate, useLocation } from 'react-router-dom';

import Google from 'assets/images/profile/google.svg';

import { supabase } from 'utils/supabaseClient';
import ROUTES from 'utils/routes.js';
import { fetchUserAccessState, getAccessDestination, sanitizeInternalRedirectPath } from 'utils/subscription';

import { useUser } from 'utils/userContext';

function Login() {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const redirectTo = sanitizeInternalRedirectPath(params.get('redirectTo'));

    const { user, endGuest, reloadUser } = useUser();

    const navigatePage = useNavigate();

    const emailInput = React.useRef(null);
    const passwordInput = React.useRef(null);

    const [errorMessage, setErrorMessage] = React.useState("");
    const [error, setError] = React.useState(false);

    const [loading, setLoading] = React.useState(false);
    const [googleLoading, setGoogleLoading] = React.useState(false);

    const resolvePostLoginDestination = React.useCallback(async (authId) => {
        let attempts = 0;
        const maxWaitAttempts = 20;

        while (attempts < maxWaitAttempts) {
            try {
                const userData = await fetchUserAccessState(supabase, authId);
                if (userData) {
                    return getAccessDestination(userData, redirectTo, ROUTES.LESSONS);
                }
            } catch (e) {
                // User row may not be ready yet.
            }

            await new Promise(resolve => setTimeout(resolve, 200));
            attempts++;
        }

        return getAccessDestination(null, redirectTo, ROUTES.LESSONS);
    }, [redirectTo]);

    const loginEmail = () => {
        if (loading) {
            console.warn("Login already in progress, ignoring duplicate request.");
            return;
        }


        const email = emailInput?.current?.value || '';
        const password = passwordInput?.current?.value || '';
        if (!email.trim() || !password.trim()) {
            console.error("Email and password are required for login.");
            setErrorMessage("Email and password are required for login.");
            setError(true);
            return;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email.trim())) {
            console.error("Invalid email format.");
            setErrorMessage("Please enter a valid email address.");
            setError(true);
            return;
        }
        if (password.trim().length < 6) {
            console.error("Password must be at least 6 characters long.");
            setErrorMessage("Password must be at least 6 characters long.");
            setError(true);
            return;
        }

        setLoading(true);
        setError(false);
        console.log("Starting email login...");
        supabase.auth.signInWithPassword({
            email: email,
            password: password,
        }).then(({ data, error }) => {
            if (error) {
                console.error("Error logging in with email:", error);
                setErrorMessage(error.message || "An error occurred during login.");
                setError(true);
                return;
            }
            console.log("Email login successful, user:", data.user);
            setTimeout(async () => {
                console.log("Navigating to lessons after email login.");

                // Switch to Google loading spinner for the user reload phase
                setLoading(false);
                setGoogleLoading(true);

                // Clear concept localStorage for all users
                for (let key in localStorage) {
                    if (key.startsWith("kodeo.concept-")) {
                        localStorage.removeItem(key);
                    }
                }

                if (user?.isGuest) {
                    // Clear guest data first, then end guest mode
                    localStorage.removeItem('kodeo.guestUser');
                    await endGuest();
                    await new Promise(resolve => setTimeout(resolve, 200));
                }

                // Force reload and wait for completion
                await reloadUser();

                const destination = await resolvePostLoginDestination(data.user.id);

                await reloadUser();
                await new Promise(resolve => setTimeout(resolve, 200));

                navigatePage(destination);
            }, 500);
        }).catch((err) => {
            console.error("Unexpected error during email login:", err);
            setErrorMessage(
                err.message?.includes("Failed to fetch") ?
                    "Unable to connect. Check your internet." :
                    err.message || "An unexpected error occurred during login."
            );
            setError(true);
        }).finally(() => {
            setLoading(false);
            setGoogleLoading(false);
        });
    };

    const googleLogin = async () => {
        if (googleLoading) {
            console.warn("Google login already in progress, ignoring duplicate request.");
            return;
        }

        console.log("Starting Google login...");
        setError(false);

        try {
            const { data: originalSessionData } = await supabase.auth.getSession();
            const originalAccessToken = originalSessionData.session?.access_token || null;

            const popup = window.open(
                ROUTES.AUTH_START,
                'google',
                `width=500,height=600,left=${window.innerWidth / 2 - 250},top=${window.innerHeight / 2 - 300}`
            );

            if (!popup) {
                console.error("Popup blocked or failed to open.");
                setErrorMessage("Popup blocked. Please allow popups and try again.");
                setError(true);
                return;
            }

            const maxAttempts = 20; // 5 seconds (20 * 250ms)
            let attempt = 0;
            let navigated = false;

            const pollForSession = setInterval(async () => {
                attempt++;
                if (popup.closed) {
                    const { data: { session } } = await supabase.auth.getSession();
                    const newAccessToken = session?.access_token;
                    const tokenChanged = newAccessToken && newAccessToken !== originalAccessToken;
                    const newLogin = !originalAccessToken && newAccessToken;
                    if ((tokenChanged || newLogin) && !navigated) {
                        navigated = true;
                        clearInterval(pollForSession);
                        console.log("Navigating to lessons after Google login.");

                        // Start loading spinner now that authentication is complete
                        setGoogleLoading(true);

                        for (let key in localStorage) {
                            if (key.startsWith("kodeo.concept-")) {
                                localStorage.removeItem(key);
                            }
                        }
                        if (user?.isGuest && session?.user) {

                            try {
                                console.log("ABout to update")
                                // Fetch existing progress for the user in Supabase
                                const { data: existingUser, error } = await supabase
                                    .from('users')
                                    .select('auth_id, currentUnit, currentConcept, currentConceptProgress')
                                    .eq('auth_id', session.user.id)
                                    .single();

                                if (error) throw error;

                                const allDefaults =
                                    existingUser.currentUnit === 1 &&
                                    existingUser.currentConcept === 1 &&
                                    existingUser.currentConceptProgress === 1;

                                if (allDefaults) {
                                    await supabase.from('users').update({
                                        currentUnit: user.currentUnit,
                                        currentConcept: user.currentConcept === 'test' ? 999 : user.currentConcept,
                                        currentConceptProgress: user.currentConceptProgress,
                                    }).eq('auth_id', session.user.id);
                                }
                            } catch (e) {
                                console.error('Failed to update guest progress:', e);
                            }

                            // Clear guest data first, then end guest mode
                            localStorage.removeItem('kodeo.guestUser');
                            await endGuest();

                            // Add delay to ensure guest cleanup is complete
                            await new Promise(resolve => setTimeout(resolve, 200));
                        }

                        // Force reload user data and wait for it to complete
                        await reloadUser();

                        const destination = await resolvePostLoginDestination(session.user.id);

                        // Final reload to ensure user context is updated
                        await reloadUser();
                        await new Promise(resolve => setTimeout(resolve, 200));

                        navigatePage(destination);
                    } else if (attempt >= maxAttempts) {
                        clearInterval(pollForSession);
                    }
                } else if (attempt >= maxAttempts) {
                    clearInterval(pollForSession);
                }
            }, 250);
        } catch (error) {
            console.error("Google login error:", error);
            setErrorMessage("Google login failed. Please try again.");
            setError(true);
        } finally {
            setGoogleLoading(false);
        }
    };

    return (
        <div className="flex flex-col items-center h-screen min-h-fit gap-4 bg-background  px-4 break-small:px-20 break-big:px-40" style={{ background: 'linear-gradient(to bottom, var(--background) 0%, var(--background) 15%, var(--primary) 650%)' }}>
            {/* Google Login Spinner Overlay */}
            {googleLoading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className='flex items-center justify-center flex-col'>
                        <div className="loader relative">
                            <div className="spinner w-28 h-28 rounded-full animate-spin border-t-[6px] border-b-[6px] border-white">
                            </div>
                            <img src={FitLogo} alt="Loading..." className='absolute inset-0 m-auto p-4' />
                        </div>
                        <p className="text-center text-white p-4 font-bold">Completing login...</p>
                    </div>
                </div>
            )}

            <nav className="w-full flex items-center justify-between py-5 z-10" >
                <Link to={ROUTES.LANDING}><img src={FullLogo} alt="Kodeo Logo" className="h-6 w-auto" /></Link>
                <div className="flex gap-4 items-center">
                    <Link to={ROUTES.SIGNUP} className="bg-background-light text-white font-bold px-6 py-2 rounded-2xl shadow-md hover:bg-background transition-all duration-500 text-lg border-2 border-primary">SIGN UP</Link>
                </div>
            </nav>
            <div className="gap-4 max-w-[480px] flex-grow select-none flex flex-col -mt-32 items-center justify-center w-full sm:w-[400px] break-big:w-[450px]">

                <div className='mb-3 text-center w-full'>
                    <h1 className="text-white text-3xl md:text-3xl font-bold ">
                        Continue your journey
                    </h1>
                    {user?.isGuest && <h2 className='text-text-light'>Your guest mode progress will be saved.</h2>}
                </div>
                {/* <h1 className=" select-text text-gray-400 text-xl  font-bold">
                    User: {user?.email || 'Not logged in'}
                </h1> */}
                <div className='w-full flex gap-2'>
                    {/* <button className='bg-background-light text-white font-bold px-8 w-full py-4 rounded-3xl shadow-md text-lg border-2 border-border flex justify-center items-center '>
                        <img src={Apple} alt="Apple Sign Up" className='w-7 h-7' />
                    </button> */}
                    <button onClick={googleLogin} className='bg-background-light text-white font-bold px-8 w-full py-4 rounded-3xl shadow-md text-lg border-2 border-border flex justify-center items-center gap-3'>
                        <img src={Google} alt="Google Sign Up" className='w-7 h-7' />
                        Login with Google
                    </button>
                </div>
                <div className='w-full flex items-center gap-2'>
                    <div className='flex-grow h-[2px] rounded-full bg-border '></div>
                    <span className='text-border-light text-lg font-bold'>OR</span>
                    <div className='flex-grow h-[2px] rounded-full bg-border'></div>
                </div>
                <form method='post' action='#' onSubmit={(e) => e.preventDefault()} className='relative w-full flex flex-col bg-background-light text-white py-3 rounded-3xl shadow-md text-lg border-2 border-border transition-colors duration-500 ease' style={{ borderColor: error ? 'var(--warning)' : 'var(--border)' }}>
                    <div className='absolute left-0 right-0 bottom-full text-white border-warning-dark border-2 text-lg font-bold mb-2 bg-warning py-2 px-2 text-center rounded-xl transition-opacity duration-500 ease' style={{ opacity: error ? 1 : 0 }}>{errorMessage}</div>
                    <input ref={emailInput} className='bg-transparent outline-none px-6 py-2 border-b-2 pb-4 border-border transition-colors duration-500 ease _noAutofill'
                        style={{ borderColor: error ? 'var(--warning)' : 'var(--border)' }}
                        type='email'
                        name='email'
                        autoComplete='email'
                        onChange={() => setError(false)}
                        onKeyDown={(e => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                if (passwordInput.current?.value?.length > 0) {
                                    loginEmail();
                                }
                                else {
                                    passwordInput.current?.focus();
                                }
                            } else if (e.key === 'Escape') {
                                e.preventDefault();
                                setError(false);
                            }
                        })}
                        placeholder='Email Address' />
                    <input ref={passwordInput} className='bg-transparent outline-none px-6 py-2 mt-2 _noAutofill'
                        type='password'
                        name='password'
                        autoComplete='current-password'
                        onChange={() => setError(false)}
                        onKeyDown={(e => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                loginEmail();
                            } else if (e.key === 'Escape') {
                                e.preventDefault();
                                setError(false);
                            }
                        })}
                        placeholder='Password' />
                </form>
                <Link to={ROUTES.FORGOT_PASSWORD} className='text-border-light text-lg font-bold underline'>Forgot Password?</Link>
                <button onClick={loginEmail} className='bg-primary text-background font-bold px-8 w-full py-4 rounded-3xl shadow-sm shadow-primary text-2xl text-center border-2 border-primary'>
                    LOG IN
                </button>
                <p className='text-border-light text-xs font-bold -mt-1'>By logging in, you agree to our <Link to={ROUTES.TERMS} className='underline'>Terms of Service</Link> and <Link to={ROUTES.PRIVACY} className='underline'>Privacy Policy</Link>.</p>
            </div>
        </div >
    );
}

export default Login;
